import React, { useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import {
  Building2,
  CalendarDays,
  Check,
  Copy,
  ExternalLink,
  Globe,
  Info,
  Mail,
  Phone,
  UserRound,
} from "lucide-react";

import useLocalization from "../../../../hooks/useLocalization";
import { formatAssistantMessage, markdownComponents } from "./markdownConfig";

const BULLET_RE = /^\s*[*\-•]\s+/;
const COLON_RE = /[:：]/;

// Registration numbers look like UIDAI/C/2026/0000133 — detected by shape, not
// by label text, so it keeps working when the backend replies in any language.
const REGISTRATION_RE = /\b[A-Z]{2,}\/[A-Z0-9]+\/\d{4}\/\d+\b/;
const DATE_RE = /\b\d{4}-\d{2}-\d{2}\b|\b\d{2}[/-]\d{2}[/-]\d{4}\b/;
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.]+/;
const PHONE_RE = /\b\d{6,15}\b/;
const URL_RE = /(?:https?:\/\/|www\.)[^\s<>()"']+/;

// Only used to tint the status pill. Unmatched values (any regional language)
// fall back to neutral styling rather than guessing.
const STATUS_TONES = [
  {
    match: /resolved|disposed|closed|completed/i,
    className:
      "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/25 dark:text-emerald-300 dark:border-emerald-800",
    dot: "bg-emerald-500",
    pulse: false,
  },
  {
    match: /under process|in progress|pending|process|received|registered/i,
    className:
      "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/25 dark:text-amber-300 dark:border-amber-800",
    dot: "bg-amber-500",
    pulse: true,
  },
  {
    match: /reject|withdraw|fail/i,
    className:
      "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/25 dark:text-rose-300 dark:border-rose-800",
    dot: "bg-rose-500",
    pulse: false,
  },
];

/**
 * Turns the backend's "* Label: value" status block into structured rows.
 * Returns null when the text isn't in that shape, so callers can fall back to
 * rendering the raw markdown.
 */
export const parseGrievanceStatus = (text = "") => {
  if (typeof text !== "string" || !text.trim()) return null;

  const rows = [];
  const notes = [];

  text.split("\n").forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;

    if (BULLET_RE.test(line)) {
      const withoutBullet = line.replace(BULLET_RE, "");
      const separatorIndex = withoutBullet.search(COLON_RE);

      if (separatorIndex > 0) {
        const label = withoutBullet.slice(0, separatorIndex).trim();
        const value = withoutBullet.slice(separatorIndex + 1).trim();
        if (label && value) {
          rows.push({ label, value });
          return;
        }
      }
      notes.push(withoutBullet);
      return;
    }

    notes.push(line);
  });

  if (rows.length < 2) return null;

  const heroIndex = rows.findIndex((row) => REGISTRATION_RE.test(row.value));

  return {
    hero: heroIndex >= 0 ? rows[heroIndex] : null,
    rows: heroIndex >= 0 ? rows.filter((_, i) => i !== heroIndex) : rows,
    note: notes.join(" ").trim(),
  };
};

const getStatusTone = (value) =>
  STATUS_TONES.find((tone) => tone.match.test(value)) || null;

const iconForRow = ({ label, value }) => {
  if (DATE_RE.test(value)) return CalendarDays;
  if (EMAIL_RE.test(value) || PHONE_RE.test(value)) return UserRound;
  if (URL_RE.test(value)) return Globe;
  if (/office|department|ministry/i.test(label)) return Building2;
  return Info;
};

// Renders a value with any email / phone number / URL turned into a usable link.
const LinkedValue = ({ value }) => {
  const email = value.match(EMAIL_RE)?.[0];
  const phone = value.match(PHONE_RE)?.[0];
  const rawUrl = value.match(URL_RE)?.[0];

  if (!email && !phone && !rawUrl) return <span>{value}</span>;

  const parts = [];
  let rest = value;

  const push = (matched, href, Icon, isExternal = false) => {
    const index = rest.indexOf(matched);
    if (index === -1) return;
    const before = rest.slice(0, index);
    if (before) parts.push(<span key={`t-${parts.length}`}>{before}</span>);
    parts.push(
      <a
        key={`l-${parts.length}`}
        href={href}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        className="inline-flex items-center gap-1 font-medium text-purple-600 hover:text-purple-700 underline decoration-purple-300 underline-offset-2 dark:text-purple-300 dark:hover:text-purple-200"
      >
        <Icon className="w-3.5 h-3.5" />
        {matched}
      </a>
    );
    rest = rest.slice(index + matched.length);
  };

  if (rawUrl) {
    let cleanUrl = rawUrl;
    const m = rawUrl.match(/[.,;:!?*~_`"']+$/);
    if (m) cleanUrl = rawUrl.slice(0, -m[0].length);
    const href = cleanUrl.startsWith("http") ? cleanUrl : `https://${cleanUrl}`;
    push(cleanUrl, href, ExternalLink, true);
  }
  if (email) push(email, `mailto:${email}`, Mail);
  if (phone) push(phone, `tel:${phone}`, Phone);
  if (rest) parts.push(<span key={`t-${parts.length}`}>{rest}</span>);

  return <>{parts}</>;
};

LinkedValue.propTypes = { value: PropTypes.string.isRequired };

function GrievanceStatusCard({ status }) {
  const { t } = useLocalization();
  const [isCopied, setIsCopied] = useState(false);

  if (!status) return null;

  const { hero, rows, note } = status;

  const handleCopyRegistration = async () => {
    try {
      await navigator.clipboard.writeText(hero.value);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context) — the number stays selectable.
    }
  };

  return (
    <div className="w-full select-text selection:bg-blue-500 selection:text-white">
      {hero && (
        <div className="relative overflow-hidden rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-4 dark:border-purple-900/60 dark:from-purple-950/40 dark:via-purple-900/20 dark:to-blue-950/30">
          <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-purple-600 to-pink-600" />

          <div className="flex items-start justify-between gap-3 pl-2">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-300">
                {hero.label}
              </p>
              <p className="mt-1 break-all font-mono text-lg font-bold tracking-wide text-gray-900 dark:text-white sm:text-xl">
                {hero.value}
              </p>
            </div>

            <button
              type="button"
              onClick={handleCopyRegistration}
              title={hero.label}
              className="shrink-0 rounded-xl border border-purple-200 bg-white/80 p-2 text-purple-600 transition-all hover:bg-white hover:shadow-md active:scale-95 dark:border-purple-800 dark:bg-[#232323]/80 dark:text-purple-300"
            >
              {isCopied ? (
                <Check className="h-4 w-4 text-emerald-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>

          {isCopied && (
            <p className="mt-2 pl-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
              {t("messageCopied")}
            </p>
          )}
        </div>
      )}

      <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {rows.map((row) => {
          const tone = getStatusTone(row.value);
          const Icon = iconForRow(row);

          return (
            <div
              key={`${row.label}-${row.value}`}
              className="rounded-xl border border-gray-200 bg-white/70 p-3 transition-colors hover:border-purple-200 dark:border-gray-700 dark:bg-[#1f1f1f]/60"
            >
              <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <Icon className="h-3.5 w-3.5" />
                {row.label}
              </dt>

              <dd className="mt-1 text-sm leading-relaxed text-gray-800 dark:text-gray-100">
                {tone ? (
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${tone.className}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${tone.dot} ${
                        tone.pulse ? "animate-pulse" : ""
                      }`}
                    />
                    {row.value}
                  </span>
                ) : (
                  <LinkedValue value={row.value} />
                )}
              </dd>
            </div>
          );
        })}
      </dl>

      {note && (
        <div className="mt-3 border-t border-dashed border-gray-200 pt-3 text-sm italic text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <ReactMarkdown components={markdownComponents}>
            {formatAssistantMessage(note)}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}

GrievanceStatusCard.propTypes = {
  status: PropTypes.shape({
    hero: PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
    rows: PropTypes.array,
    note: PropTypes.string,
  }),
};

export default GrievanceStatusCard;
